/**
 * Helpers for detail routes whose keys may contain `/` (e.g. "1/6 BBL Keg").
 * Single-segment `:name` params break when the browser turns %2F into `/`;
 * splat routes (`/items/*`) capture the full remainder.
 */

/** Build `/prefix/seg1/seg2` with each segment URI-encoded. */
export function buildSplatPath(prefix: string, value: string): string {
  const base = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
  const trimmed = String(value ?? '');
  if (!trimmed) return base;
  const encoded = trimmed
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/');
  return `${base}/${encoded}`;
}

/**
 * Decode a splat or param value. Tolerates already-decoded values and
 * names that contain literal `%` (avoids URIError from double-decode).
 */
export function decodeRouteValue(raw: string | undefined | null): string {
  if (raw == null) return '';
  const value = String(raw);
  if (!value.includes('%')) return value;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Prefer splat `*`, then a named param (back-compat). */
export function paramFromSplatOrName(
  params: Readonly<Record<string, string | undefined>>,
  namedKey: string
): string {
  const splat = params['*'];
  if (splat != null && splat !== '') return decodeRouteValue(splat);
  return decodeRouteValue(params[namedKey]);
}
